import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { JOTFORM_URL } from "@/constants";

const EASE = [0.16, 1, 0.3, 1];

const MaskedLine = ({ children, delay }) => (
    <span className="block overflow-hidden pb-[0.08em]">
        <motion.span
            className="block will-change-transform"
            initial={{ y: "110%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1.4, delay, ease: EASE }}
        >
            {children}
        </motion.span>
    </span>
);

const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const markY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
    const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

    return (
        <section
            id="top"
            ref={ref}
            data-testid="hero-section"
            className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-white"
        >
            {/* Royal curtains — signature on-load moment */}
            <motion.div
                className="absolute inset-x-0 top-0 z-40 bg-[#270057]"
                initial={{ height: "16vh" }}
                animate={{ height: 0 }}
                transition={{ duration: 1.6, delay: 0.2, ease: EASE }}
            />
            <motion.div
                className="absolute inset-x-0 bottom-0 z-40 bg-[#270057]"
                initial={{ height: "16vh" }}
                animate={{ height: 0 }}
                transition={{ duration: 1.6, delay: 0.2, ease: EASE }}
            />

            {/* Soft royal flare */}
            <div className="royal-flare animate-flare absolute inset-x-0 top-0 h-[70vh]" />

            {/* Hairline royal frame */}
            <motion.div
                aria-hidden="true"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1.6, delay: 2.1 }}
                className="pointer-events-none absolute inset-5 z-10 hidden border border-[#270057]/15 md:block"
            />

            {/* Content */}
            <motion.div
                style={{ opacity: fade }}
                className="relative z-10 mx-auto flex w-full max-w-[1500px] flex-col items-center px-6 py-40 text-center lg:px-16"
            >
                <motion.p
                    data-testid="hero-eyebrow"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1.2, delay: 1.2 }}
                    className="mb-10 font-display text-[10px] uppercase tracking-[0.5em] text-[#270057]"
                >
                    Vellor Studio — Post-Production Atelier
                </motion.p>

                <div className="relative">
                    <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                        <motion.img
                            src="https://i.postimg.cc/BZH2xZFD/Favicon.png"
                            alt=""
                            aria-hidden="true"
                            data-testid="hero-brand-watermark"
                            style={{ y: markY }}
                            className="w-[69vw] max-w-[900px] select-none opacity-[0.05]"
                        />
                    </div>
                    <h1
                        data-testid="hero-headline"
                        className="relative font-serif text-[13vw] font-medium leading-[1.02] tracking-[-0.02em] text-[#270057] sm:text-[10vw] lg:text-[7.2vw]"
                    >
                        <MaskedLine delay={0.6}>Elevating</MaskedLine>
                        <MaskedLine delay={0.78}>
                            <em className="font-medium italic">Cinematic</em>
                        </MaskedLine>
                        <MaskedLine delay={0.96}>Commerce</MaskedLine>
                    </h1>
                </div>

                <motion.div
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ duration: 1.4, delay: 1.5, ease: EASE }}
                    className="royal-rule mt-12 w-40"
                />

                <motion.p
                    data-testid="hero-subheadline"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1.2, delay: 1.55, ease: EASE }}
                    className="mt-10 max-w-xl text-base font-normal leading-relaxed text-[#270057] sm:text-lg"
                >
                    An exclusive post-production atelier crafting visual assets
                    for high-ticket brands.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1.2, delay: 1.75, ease: EASE }}
                    className="mt-14"
                >
                    <a
                        href={JOTFORM_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="hero-commission-project-button"
                        className="btn-royal inline-block rounded-full px-12 py-5 font-display text-[11px] font-semibold uppercase tracking-[0.3em]"
                    >
                        Commission a Project
                    </a>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1.2, delay: 2.3 }}
                    className="absolute bottom-10 left-1/2 -translate-x-1/2"
                >
                    <div className="flex flex-col items-center gap-3">
                        <span className="font-mono text-[9px] uppercase tracking-[0.4em] text-[#270057]">
                            Scroll
                        </span>
                        <span className="animate-scroll-drift block h-10 w-px bg-gradient-to-b from-[#270057]/50 to-transparent" />
                    </div>
                </motion.div>
            </motion.div>
        </section>
    );
};

export default Hero;
